import { motion } from "motion/react";
import { Building2, Calendar, FileText, ArrowRight, Target, Zap, BarChart3 } from "lucide-react";
import { ExperienceButton } from "./ExperienceButton";
import { useState, useEffect } from "react";

interface VendorIntroProps {
  vendorName: string;
  category: string;
  evaluationDate: string;
  onBegin: () => void;
}

export function VendorIntro({ vendorName, category, evaluationDate, onBegin }: VendorIntroProps) {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(
      window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window
    );
  }, []);

  return (
    <div className="min-h-screen bg-[#063F6B] text-white flex items-center justify-center px-6 relative overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#124C77]/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#EAA059]/[0.05] rounded-full blur-[100px]" />
      </div>

      <div className="max-w-4xl relative z-10">
        <div className="text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="inline-block mb-10"
          >
            <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm">
              <FileText className="w-4 h-4 text-[#EAA059]" />
              <span className="text-sm text-white/80 font-medium tracking-wide">Evaluation Report</span>
            </div>
          </motion.div>

          {/* Vendor Name */}
          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-4xl sm:text-6xl lg:text-7xl font-semibold mb-8 leading-[1.1] tracking-tight"
          >
            {vendorName}
          </motion.h1>

          {/* Gold accent divider */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="h-[2px] bg-gradient-to-r from-transparent via-[#EAA059] to-transparent mb-8 mx-auto"
          />

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="space-y-3 mb-14"
          >
            <div className="flex items-center justify-center gap-3 text-lg text-slate-300/90">
              <Building2 className="w-5 h-5 text-[#EAA059]/80" />
              <span>{category}</span>
            </div>
            <div className="flex items-center justify-center gap-3 text-slate-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Evaluated {evaluationDate}</span>
            </div>
          </motion.div>

          {/* Info cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="grid md:grid-cols-3 gap-5 mb-14"
          >
            {[
              { icon: Target, title: "6 Criteria", subtitle: "Comprehensive Analysis" },
              { icon: Zap, title: "Real-Time", subtitle: "Interactive Experience" },
              { icon: BarChart3, title: "Data-Driven", subtitle: "Strategic Insights" },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 + index * 0.1, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  className="rounded-2xl p-6 border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm text-center hover:bg-white/[0.07] transition-colors duration-300"
                >
                  <div className="w-11 h-11 bg-gradient-to-br from-[#EAA059] to-[#d4893e] rounded-xl flex items-center justify-center mb-3 mx-auto shadow-md shadow-[#EAA059]/15">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-lg font-semibold mb-1">{item.title}</div>
                  <div className="text-sm text-slate-400">{item.subtitle}</div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ExperienceButton
              onClick={onBegin}
              className="px-10 py-4 bg-gradient-to-r from-[#EAA059] to-[#d4893e] hover:from-[#d4914d] hover:to-[#c07830] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30"
            >
              Begin Deep Dive
              <ArrowRight className="ml-3 w-5 h-5" />
            </ExperienceButton>
          </motion.div>

          {/* Hint */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 1.6 }}
            className="mt-10 text-sm text-slate-500"
          >
            {isTouchDevice ? (
              "Tap anywhere to continue"
            ) : (
              <>
                Press <kbd className="px-2 py-0.5 bg-white/[0.06] border border-white/[0.08] rounded text-xs">Space</kbd> to continue
              </>
            )}
          </motion.p>
        </div>
      </div>
    </div>
  );
}
